import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import FocusTrap from 'focus-trap-react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Button } from './Button.js';
import { cn } from '../lib/utils.js';
import { disabledFade, focusRing } from '../lib/focus.js';
import { FiltersGroup } from './FiltersGroup.js';

/* ===========================================================================
 * FiltersPopover — the toolbar's second slot, and the retirement of a pattern.
 *
 * §2 of docs/design-rules.md is explicit twice over: "Filters always collapse
 * into a popover with a count badge — never a row of stacked label+control
 * pairs. That was the old pattern and it is retired." §6 states the general form
 * of the same rule: do not build a second control language; every filter goes in
 * the one toolbar.
 *
 * That is a stronger claim than it looks, so it is worth saying why it is right
 * rather than treating it as taste. A row of stacked label+control pairs has
 * three properties a popover does not:
 *
 *   - it grows the toolbar with the number of filters, so the bar is a different
 *     height on every screen and the table below it starts in a different place;
 *   - it shows every filter at full weight whether or not it is doing anything,
 *     so a screen with nine filters and none applied looks as busy as one with
 *     nine applied — and the reader cannot tell which case they are in;
 *   - it has nowhere to put the ANSWER to "what is currently narrowing this
 *     list", which is the only question a reader actually has.
 *
 * The count badge is that answer, and it is why the trigger carries one. "2"
 * beside the word Filters is a whole sentence: two things are hiding rows from
 * you, and here is where they are. A count of zero draws NO badge — a badge
 * reading "0" is a control claiming to be doing something.
 *
 * ---------------------------------------------------------------------------
 * THE PANEL IS A `dialog`, TRAPPED AND ESCAPABLE.
 *
 * A popover holding chips, selects, switches and an Apply button is a small
 * form, and a small form the Tab key can walk out of — leaving focus behind a
 * panel that has no overlay to click, with no way back — is what makes keyboard
 * users abandon a filter UI. `packages/ui` already depends on `focus-trap-react`
 * and `Dialog.tsx` already settled the option set; both are reused rather than
 * re-derived.
 *
 * Three differences from `Dialog`, all because this is ANCHORED and not modal:
 *
 *   - `allowOutsideClick`, plus our own outside-pointerdown handler. A modal
 *     eats outside clicks; a popover is dismissed BY them, because a reader who
 *     clicks the table has answered the question.
 *   - no body-scroll lock and no scrim. The page behind a filter panel is the
 *     thing being filtered — dimming it defeats the point.
 *   - focus returns to the TRIGGER on Escape, which makes Escape a round trip
 *     rather than a way to lose your place. It deliberately does NOT return on
 *     an outside click: pulling the caret back to the trigger after someone
 *     clicked a row would take focus off the thing they just chose.
 *
 * ---------------------------------------------------------------------------
 * ENTRANCE. `--ease-entrance`, and no new keyframe.
 *
 * The token layer's one entrance keyframe is `dialog-in`, which travels a full
 * height up from the bottom edge — right for a sheet, wrong for a panel hanging
 * 8px under a button. Rather than add a second keyframe to a preset three apps
 * import, the entrance is a TRANSITION flipped on the frame after mount: the
 * panel renders at `opacity-0 -translate-y-1` and lands on the next frame. Same
 * curve, same duration band, nothing added to the shared file.
 *
 * `-translate-y-1` is on the BLOCK axis, so it needs no mirroring — the panel
 * arrives from above in both writing directions.
 *
 * ---------------------------------------------------------------------------
 * WHY THE GROUP HEADINGS ARE NOT THE MOCK'S 11px CAPS. All four toolbar mocks
 * set them at 11px / 600 / uppercase / +.6px. §1 and §6 of the rules file ban
 * exactly that for a label, on the grounds that at that size caps read as
 * texture — and a filter group heading is a label for the controls under it.
 * `text-label` (12.5px, sentence case) is what the rules prescribe, and §0 of
 * docs/design-system-migration.md already settled the identical disagreement for
 * table headers in the rules file's favour. The mock's 11px caps survive only on
 * card-internal eyebrow captions, which this is not.
 *
 * RTL: `start-0`, `ms-auto`, logical padding. No physical `left`/`right`.
 * ======================================================================== */

export interface FiltersPopoverProps {
  /**
   * The panel's heading — "Filter employees", "Filter the queue". Also the
   * panel's accessible name, so the two cannot disagree.
   */
  heading: string;
  /** The trigger's label. Defaults to "Filters"; pass a translated string. */
  label: string;
  /**
   * How many filters are currently applied. `0` or `undefined` draws no badge.
   */
  count?: number;
  /**
   * What the count means for a reader who never sees the badge — "2 filters
   * applied". Defaults to the bare number, which is better than nothing and
   * worse than a sentence, so pass one.
   */
  countLabel?: string;
  /** The filter controls. Group them with `FiltersPopover.Group`. */
  children: ReactNode;
  /** Clears every filter. The link is omitted entirely when not passed. */
  onReset?: () => void;
  resetLabel: string;
  /**
   * Commits and closes. Omit it when the filters apply as they are changed — in
   * which case the panel is dismissed by Escape, an outside click or the
   * trigger, and a full-width Apply would be a fourth way to do nothing. (Same
   * rule `Dialog.tsx` states for a form dialog's Cancel.)
   */
  onApply?: () => void;
  applyLabel?: string;
  /** Controlled open state. Leave both undefined to let the component own it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Extra classes for the anchor, not the panel. */
  className?: string;
}

export function FiltersPopover({
  heading,
  label,
  count,
  countLabel,
  children,
  onReset,
  resetLabel,
  onApply,
  applyLabel = 'Apply filters',
  open: controlledOpen,
  onOpenChange,
  className,
}: FiltersPopoverProps): JSX.Element {
  const panelId = useId();
  const headingId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const restoreFocus = useRef(false);
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange]
  );

  /**
   * Close AND put the caret back on the trigger — the keyboard round trip that
   * Escape and Apply both take. An outside click calls `setOpen(false)` directly
   * instead, because pulling focus back to the trigger would take it off the row
   * the reader just clicked.
   *
   * THE RESTORE CANNOT HAPPEN HERE, and that is the whole reason for the flag.
   * `setOpen(false)` does not unmount anything synchronously, so a `focus()` on
   * this line runs while the panel is still mounted and the trap is still
   * active — the trap immediately yanks focus back inside, and then the panel
   * unmounts with `returnFocusOnDeactivate: false` and leaves focus on `<body>`.
   * Measured exactly that way before the flag was added. So the intent is
   * recorded and the effect below acts on it once the DOM has caught up.
   *
   * `returnFocusOnDeactivate` cannot do this job either: it fires on BOTH exits,
   * including the outside click that must not move focus.
   */
  const dismiss = useCallback(() => {
    restoreFocus.current = true;
    setOpen(false);
  }, [setOpen]);

  /**
   * THE PANEL IS PLACED IN THE VIEWPORT, NOT IN ITS PARENT.
   *
   * It used to be `absolute top-[2.875rem] start-0` inside the anchor, which is
   * correct until an ancestor has `overflow-hidden` — and then the panel is
   * clipped by a box that knows nothing about it. That is not hypothetical:
   * the operations rail is an `overflow-hidden rounded-panel` capped at
   * `max-h-72` when it docks, so a 316px panel hanging 46px below its trigger
   * is cut off inside it, and the screen there had to reach for a native
   * `<select>` instead of the control §2 prescribes.
   *
   * §4 already names this family of bug for Leaflet — a child that escapes, or
   * fails to escape, a stacking or clipping context its parent never declared
   * for it. The answer there was to stop the map's numbers leaking OUT; the
   * answer here is the mirror of it: take the panel out of the flow entirely,
   * so no ancestor's `overflow` can reach it.
   *
   * `position: fixed` in a portal on `document.body`, placed from the trigger's
   * own rect each time it opens and on any scroll or resize while it is open.
   * The listeners are capture-phase and passive: a popover anchored to a
   * control inside a scrolling table has to travel with that control, and
   * `true` catches scrolls on ancestors rather than only on the window.
   */
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    function place(): void {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const t = trigger.getBoundingClientRect();
      const p = panel.getBoundingClientRect();
      /* The 8px the panel hangs below the trigger, and the 8px it keeps off
         the edge of the glass. Geometry read at runtime, not a design step —
         the token layer owns the panel's box, not where the viewport ends. */
      const GAP = 8;
      const EDGE = 8;
      const rtl = getComputedStyle(trigger).direction === 'rtl';

      /* THE PANEL IS NEVER TALLER THAN THE ROOM IT HAS.
         Below by default; above only when below genuinely does not fit and
         above is roomier — a panel that flips on every near-miss is worse than
         one that occasionally sits low.
         The case that matters is the third one: a long filter set on a laptop
         fits NEITHER way. Left to run past the bottom of the glass its last
         controls — and its Reset — are simply unreachable, with no scrollbar to
         say so, because a `fixed` element does not lengthen the page. So the
         chosen side's room becomes a max-height and the panel scrolls inside
         itself. `scrollHeight` is what is measured, not the rect: the rect is
         already constrained by the max-height WE set last time, and measuring
         it would ratchet the panel smaller on every scroll event. */
      const below = window.innerHeight - t.bottom - GAP - EDGE;
      const above = t.top - GAP - EDGE;
      const natural = panel.scrollHeight;
      const useAbove = natural > below && above > below;
      // 9rem: below this a filter panel is a scroll-hole rather than a control,
      // and the reader is better served by it overhanging slightly.
      const room = Math.max(144, useAbove ? above : below);
      const height = Math.min(natural, room);
      const top = useAbove
        ? Math.max(EDGE, t.top - GAP - height)
        : Math.min(t.bottom + GAP, Math.max(EDGE, window.innerHeight - EDGE - height));
      // Aligned to the trigger's INLINE-START edge, so under Arabic the panel
      // drops from the other side — what `start-0` used to do for us.
      let left = rtl ? t.right - p.width : t.left;
      left = Math.min(Math.max(EDGE, left), Math.max(EDGE, window.innerWidth - p.width - EDGE));
      setPos({ top, left, maxHeight: room });
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (open || !restoreFocus.current) return;
    restoreFocus.current = false;
    triggerRef.current?.focus();
  }, [open]);

  // The entrance flip. Keyed on `open` so it re-arms every time the panel
  // mounts, and reset on close so the next open starts from `opacity-0` rather
  // than appearing fully formed.
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      // `stopPropagation` so Escape closes this panel and not also whatever is
      // behind it — a filter popover opened inside a dialog should not take the
      // dialog down with it.
      e.stopPropagation();
      dismiss();
    }
    function onPointerDown(e: Event): void {
      const node = e.target;
      /* The panel is PORTALLED, so it is no longer inside the anchor — without
         the second test every click on a control inside the panel would read as
         a click outside it and shut the panel on its own contents. */
      if (node instanceof Node && anchorRef.current?.contains(node)) return;
      if (node instanceof Node && panelRef.current?.contains(node)) return;
      /* AND THE PANEL'S OWN CHILDREN MAY PORTAL TOO — one level further out
         than the note above anticipated.
         `Combobox` renders its listbox on `document.body` so a scrolling,
         height-capped panel cannot clip it. That list is logically inside this
         popover and physically a sibling of it, so `panelRef.contains` says
         false and, without this, picking a department would be read as a click
         outside and shut the panel on the very choice it was opened to make.
         The clipping fix would have traded one broken filter for another.
         Marked on the surface rather than wired through refs between two
         components that do not know each other: any portalled surface that
         belongs to whatever opened it carries `data-portal-surface`, and every
         dismiss-on-outside-click handler can honour it without either side
         importing the other. */
      if (node instanceof Element && node.closest('[data-portal-surface]')) return;
      setOpen(false);
    }
    // Capture phase, so the panel closes even when the click lands on something
    // that stops propagation on its way up (a table row's own handler).
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, dismiss, setOpen]);

  return (
    // `relative` anchors the panel, and the anchor is also what the outside-click
    // test measures against — so a click on the TRIGGER is not "outside" and
    // cannot reopen what it just closed.
    <div ref={anchorRef} className={cn('relative flex-none', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        onClick={() => (open ? dismiss() : setOpen(true))}
        className={cn(
          // `.state-layer` rather than a hand-written `hover:bg-*`: it is the
          // same currentColor hover/focus/press ramp `Button` and `IconButton`
          // use, so this control darkens and presses like the rest of the family
          // in either theme and for any tenant hue.
          'state-layer inline-flex items-center gap-element whitespace-nowrap',
          'min-h-control rounded-control border border-border bg-surface px-3',
          // 13.5px at 600 — the trigger is a toolbar BUTTON and takes the body
          // step the buttons either side of it take (`font-size: 13.5px;
          // font-weight: 600`, Hadir Operations.dc.html:237). `text-label` put
          // it at 12.5px, the one control in the bar reading a step small. The
          // weight is separate because `text-copy` is the 400 body step.
          'text-copy font-semibold text-foreground',
          '[@media(pointer:coarse)]:min-h-touch',
          'transition-[background-color,border-color] duration-fast ease-standard',
          // The handoff's hover sharpens the EDGE rather than shouting with the
          // fill, which is what keeps a toolbar of five controls quiet.
          'hover:border-border-strong',
          focusRing,
          cn('disabled:pointer-events-none disabled:transition-none', disabledFade)
        )}
      >
        <SlidersHorizontal size={15} aria-hidden="true" className="flex-none" />
        {label}
        {count != null && count > 0 && (
          <span
            className={cn(
              'inline-grid flex-none place-items-center rounded-xs px-1',
              // Minimum, not fixed — a three-digit count grows the pill instead
              // of overflowing it.
              'min-h-[1.125rem] min-w-[1.125rem]',
              // The brand `tint`/`deep` pairing: a pale wash with brand ink on
              // it. Never the solid `--primary` here — this is a readout, and
              // brand is for the active nav marker, the primary action and the
              // one series meaning "on site" (rules §1).
              'bg-primary-tint text-primary-deep',
              // `text-label` (12.5px), not the mock's 11px. Same reasoning that
              // moved `Badge` off `text-badge`: this is a figure a reader reads,
              // and it should match the badges elsewhere on the screen rather
              // than being the one 11px thing in the bar.
              'text-label'
            )}
          >
            {/* The digit is for the eye and the sentence is for the ear. A bare
                "2" announced after "Filters" tells a screen-reader user nothing
                about what the 2 counts. */}
            <span aria-hidden="true">{count}</span>
            <span className="sr-only">{countLabel ?? String(count)}</span>
          </span>
        )}
        <ChevronDown size={13} aria-hidden="true" className="flex-none text-foreground-tertiary" />
      </button>

      {open &&
        createPortal(
          <FocusTrap
            focusTrapOptions={{
            // Escape is handled above, so the trap must not also act on it —
            // its own deactivate path would skip the focus restore.
            escapeDeactivates: false,
            // The outside click is handled above too. The trap has to let it
            // through rather than swallowing it, or the first click outside the
            // panel does nothing at all.
            clickOutsideDeactivates: false,
            allowOutsideClick: true,
            // The panel can render before it has a focusable child (a group
            // whose controls are still loading), and a trap with nothing to
            // focus throws. It falls back to the panel itself, which is why the
            // panel carries `tabIndex={-1}`.
            fallbackFocus: () => panelRef.current ?? document.body,
            returnFocusOnDeactivate: false,
          }}
        >
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-labelledby={headingId}
            tabIndex={-1}
            /* Placed by the layout effect. Until it has measured, the panel is
               parked off-screen rather than flashed at 0,0 — `useLayoutEffect`
               runs before paint, so this is what the browser draws only if the
               trigger has somehow gone away. */
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              // Set only once measured: an unbounded first paint is what lets
              // `scrollHeight` report the panel's natural height above.
              ...(pos ? { maxHeight: pos.maxHeight, overflowY: 'auto' as const } : {}),
            }}
            className={cn(
              // Placed by the effect above, in the viewport rather than in the
              // anchor. `z-40` still, but now against `document.body` — there
              // is no ancestor left to be trapped under.
              'fixed z-40',
              // 316px, the handoff's width, capped at `--popover-max` (352px) —
              // the width role the token layer documents for "something anchored
              // to the control that opened it".
              'w-[19.75rem] max-w-popover',
              // `border-subtle`, not `border`: the panel is a CONTAINER, and the
              // handoff draws a container's edge one step lighter than a
              // control's — `1px solid #E7E3DB` here (Hadir
              // Operations.dc.html:246) against the #DDD8CE on the trigger above
              // it. See the Boundaries note in apps/web/src/index.css.
              'rounded-toolbar border border-border-subtle bg-surface p-3.5 shadow-popover',
              'focus-visible:outline-none',
              // The entrance — see the header note on why a transition and not a
              // keyframe. `motion-reduce` drops the travel, not the panel.
              'transition-[opacity,transform] duration-fast ease-entrance motion-reduce:transition-none',
              entered ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
            )}
          >
            <div className="mb-component flex items-center gap-element">
              {/* NOT `text-h3` (17px). A 17px heading inside a 316px panel
                  hanging off a button reads as a card title — it claims more of
                  the page than a popover has. The handoff sets it at
                  `font-family: 'Archivo'; font-weight: 600; font-size: 13.5px`
                  (Hadir Operations.dc.html:247): the display face and the
                  heading weight at the body step, which is what makes it a
                  LABEL FOR THE PANEL rather than a section of the page.
                  `font-display` has to be said here because the family rides on
                  the five display type steps, and `text-copy` is not one. */}
              <div id={headingId} className="font-display text-copy font-semibold">
                {heading}
              </div>
              {onReset && (
                <button
                  type="button"
                  onClick={onReset}
                  className={cn(
                    // A text link, NOT a `<Button variant="ghost">`. Reset is a
                    // way back; a Button — even ghost — brings a control's
                    // padding, its press scale and its state layer, which would
                    // put "undo my filtering" at the same weight as Apply. Two
                    // controls of equal weight for two decisions of very unequal
                    // importance is the rule `Dialog.tsx` states for footers.
                    'ms-auto rounded-2xs px-1 py-0.5 text-label text-primary',
                    'transition-colors duration-fast ease-standard hover:text-primary-hover',
                    focusRing
                  )}
                >
                  {resetLabel}
                </button>
              )}
            </div>

            {/* Groups are separate questions, so the distance between them is
                the component step (16px) while the controls inside one are at
                the element step (8px). Collapse the two and the panel reads as
                one long list of chips with headings sprinkled through it. */}
            <div className="flex flex-col gap-component">{children}</div>

            {onApply && (
              // The real `Button` primitive, full width. FULL WIDTH is the point
              // and is the one place in the system a button is: the panel is
              // 316px of questions and this is its single answer, so an
              // inline-sized button in the corner would read as optional.
              //
              // Using `Button` rather than a hand-rolled fill is what keeps the
              // press ramp, the focus ring, the coarse-pointer floor and the
              // disabled treatment identical to every other primary action —
              // this component has no business owning a second version of them.
              <Button
                type="button"
                onClick={() => {
                  onApply();
                  // Apply is a commit, so it closes — and it uses the same
                  // round trip Escape does, putting the caret back on the
                  // trigger. A keyboard reader who applies a filter should end
                  // up on the control that now reads "Filters 3", not adrift on
                  // a button that no longer exists.
                  dismiss();
                }}
                className="mt-component w-full"
              >
                {applyLabel}
              </Button>
            )}
          </div>
        </FocusTrap>,
          document.body
        )}
    </div>
  );
}

FiltersPopover.Group = FiltersGroup;
